import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment/environment';
import type { CorrectionRequestDTO } from '@libs/dtos/merit/candidate-approval.dto';
import type { CvExtractionDTO } from '@libs/dtos/merit/cv-extraction.dto';
import type {
	CandidateSignupStartDTO,
	OnboardingLinkDTO,
	OnboardingLinkStatusDTO,
	OnboardingStateDTO,
	OnboardingStep,
	OnboardingSubmitResponseDTO,
	OnboardingUploadResponseDTO,
} from '@libs/dtos/merit/onboarding.dto';
import { map, type Observable, switchMap } from 'rxjs';

/** The three steps whose payload is a file instead of JSON (M02 §3). */
export type OnboardingUploadKind = 'photo' | 'cv' | 'video';

/**
 * Resultado de una subida: el estado refrescado y, solo para el CV, el id de la
 * lectura con IA que acaba de arrancar (MD-Onboarding-Candidato-CV-IA §5).
 */
export interface OnboardingUploadResult {
	state: OnboardingStateDTO;
	extractionId?: number;
}

/**
 * The wizard's data source. Both modes speak the same four calls, so the
 * component swaps the gateway and never branches on "public vs authenticated"
 * (M02 §5 — one component, one form, two origins).
 */
export interface OnboardingGateway {
	/** True when the credential is the URL token instead of the session. */
	readonly isPublic: boolean;
	load(): Observable<OnboardingStateDTO>;
	saveStep(step: OnboardingStep, payload: unknown): Observable<OnboardingStateDTO>;
	/**
	 * Stores the file and answers with the refreshed state. The upload endpoint
	 * itself returns only `{ step, file }`, so the state is re-read here — the
	 * upload also moves `completedSteps` / `canSubmit`, which the wizard needs.
	 */
	upload(kind: OnboardingUploadKind, file: File, durationSeconds?: number | null): Observable<OnboardingUploadResult>;
	/** Estado de una lectura de CV: `processing` hasta que la IA termina. */
	cvExtraction(id: number): Observable<CvExtractionDTO>;
	/** El candidato acepta el prerrelleno propuesto por la IA. */
	applyCvExtraction(id: number): Observable<CvExtractionDTO>;
	submit(): Observable<OnboardingSubmitResponseDTO>;
}

/**
 * Reads the real duration of a video in the browser before uploading it, so a
 * clip outside the 60–90 s window is rejected without spending the upload
 * (M02 §6). Resolves null when the browser cannot decode the metadata — the
 * server re-checks it anyway, so an unknown duration is not a hard stop.
 */
export function probeVideoDuration(file: File): Promise<number | null> {
	return new Promise((resolve) => {
		if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') {
			resolve(null);
			return;
		}
		const url = URL.createObjectURL(file);
		const video = document.createElement('video');
		let settled = false;
		const done = (seconds: number | null) => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			URL.revokeObjectURL(url);
			video.removeAttribute('src');
			resolve(seconds);
		};
		// Some containers never fire loadedmetadata (unsupported codec): give up
		// rather than leaving the dropzone spinning forever.
		const timer = setTimeout(() => done(null), 8000);
		video.preload = 'metadata';
		video.onloadedmetadata = () => {
			const duration = video.duration;
			done(Number.isFinite(duration) && duration > 0 ? duration : null);
		};
		video.onerror = () => done(null);
		video.src = url;
	});
}

/**
 * M02 · candidate onboarding.
 *
 * Two entry points for the very same wizard:
 * - `forToken()` — public URL, no login; the token in the path is the credential.
 * - `forCurrentUser()` — verified candidate, authenticated by the session.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingService {
	private readonly http = inject(HttpClient);
	private readonly base = environment.pathBackend;

	/** Public URL mode: `/onboarding/:token` (M02 §4). */
	forToken(token: string): OnboardingGateway {
		return this.gateway(`${this.base}/onboarding/${encodeURIComponent(token)}`, true);
	}

	/** Authenticated mode: the candidate's own profile (M02 §4). */
	forCurrentUser(): OnboardingGateway {
		return this.gateway(`${this.base}/me/profile`, false);
	}

	/**
	 * Public signup (23/09): creates the draft the wizard saves into and returns
	 * its token, from which the page goes on in `forToken()` mode. `token` is null
	 * only when the server took the request for a bot's.
	 */
	startSignup(body: CandidateSignupStartDTO): Observable<{ token: string | null }> {
		return this.http.post<{ token: string | null }>(`${this.base}/onboarding/signup/start`, body);
	}

	// =========================================================================
	// Admin — the public link (perm `onboarding:link`)
	// =========================================================================

	/** Is there a live link for this candidate, and until when. Never the token. */
	linkStatus(candidateId: number): Observable<OnboardingLinkStatusDTO> {
		return this.http.get<OnboardingLinkStatusDTO>(`${this.base}/onboarding/candidates/${candidateId}/link`);
	}

	/**
	 * Mints the link and returns it **once**. The server revokes the previous one
	 * by default, so calling this on a candidate who already has a live link
	 * silently kills the URL they were sent — the screen warns before doing it.
	 */
	generateLink(candidateId: number): Observable<OnboardingLinkDTO> {
		return this.http.post<OnboardingLinkDTO>(`${this.base}/onboarding/candidates/${candidateId}/link`, {});
	}

	// =========================================================================
	// Admin — «Correcciones solicitadas» (27/10)
	// =========================================================================

	/** Every correction request sent to this candidate, newest first. */
	correctionRequests(candidateId: number): Observable<CorrectionRequestDTO[]> {
		return this.http.get<CorrectionRequestDTO[]>(
			`${this.base}/onboarding/candidates/${candidateId}/correction-requests`,
		);
	}

	/**
	 * Emails the candidate what to fix, with a fresh link to the wizard. The old
	 * link stops working. Returns the updated list.
	 */
	requestCorrections(candidateId: number, message: string): Observable<CorrectionRequestDTO[]> {
		return this.http.post<CorrectionRequestDTO[]>(
			`${this.base}/onboarding/candidates/${candidateId}/correction-requests`,
			{ message },
		);
	}

	/** Kills every outstanding link of this candidate. */
	revokeLink(candidateId: number): Observable<{ revoked: number }> {
		return this.http.post<{ revoked: number }>(`${this.base}/onboarding/candidates/${candidateId}/link/revoke`, {});
	}

	private gateway(root: string, isPublic: boolean): OnboardingGateway {
		const http = this.http;
		const load = () => http.get<OnboardingStateDTO>(root);
		return {
			isPublic,
			load,
			saveStep: (step, payload) => http.patch<OnboardingStateDTO>(`${root}/step/${step}`, payload ?? {}),
			upload: (kind, file, durationSeconds) => {
				const body = new FormData();
				body.append('file', file, file.name);
				// The server re-measures, but sending what the browser read keeps the
				// two in sync when the container has no seekable metadata.
				if (durationSeconds != null) body.append('durationSeconds', String(Math.round(durationSeconds)));
				return http
					.post<OnboardingUploadResponseDTO>(`${root}/${kind}`, body)
					.pipe(
						switchMap((response) =>
							load().pipe(map((state) => ({ state, extractionId: response.extractionId }))),
						),
					);
			},
			cvExtraction: (id) => http.get<CvExtractionDTO>(`${root}/cv/extraction/${id}`),
			applyCvExtraction: (id) => http.post<CvExtractionDTO>(`${root}/cv/extraction/${id}/apply`, {}),
			submit: () => http.post<OnboardingSubmitResponseDTO>(`${root}/submit`, {}),
		};
	}
}
